import { Link } from "@tanstack/react-router";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export function DashboardMock({ onOpenDeal }: { onOpenDeal?: () => void }) {
  const deals: Array<{
    name: string;
    delivered?: string;
    status: "Delivered" | "Running";
  }> = [
    { name: "Solstice BioTherapeutics", delivered: "100 delivered", status: "Delivered" },
    { name: "Deal 2026-05-24 14:24", delivered: "10 delivered", status: "Delivered" },
  ];
  return (
    <div className="rounded-md bg-brand-paper-2 p-3.5">
      <div className="mb-1">
        <div className="font-serif text-[15px] text-brand-ink-2">Good afternoon</div>
        <div className="font-serif text-[28px] tracking-[-0.01em] text-brand-ink">Alex.</div>
      </div>
      {/* Credits remaining — navy panel */}
      <div className="mt-3 rounded-md bg-brand-ink px-3.5 py-3 text-brand-paper">
        <div className="flex items-center justify-between text-[9.5px] uppercase tracking-[0.12em] text-brand-paper/60">
          <span>Credits remaining</span>
        </div>
        <div className="mt-1 flex items-baseline justify-between">
          <div className="font-mono text-[20px] font-semibold tabular-nums">
            890 <span className="text-[12px] text-brand-paper/60">/ 1100</span>
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-paper/15">
          <div className="h-full rounded-full bg-brand-accent" style={{ width: "80.9%" }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[14px] text-brand-paper/70">
          <span>210 used this cycle</span>
          <span className="text-brand-accent">Top up →</span>
        </div>
      </div>
      {/* Start new deal */}
      <button
        type="button"
        onClick={onOpenDeal}
        className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-md bg-brand-ink px-3.5 py-3 text-[13.5px] font-medium text-brand-paper transition-opacity hover:opacity-90"
      >
        <span>Start a new deal</span>
        <span aria-hidden>→</span>
      </button>
      {/* Recent deals */}
      <div className="mt-4">
        <p className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.14em] text-brand-accent">
          01 Recent deals
        </p>
        <ul className="space-y-1.5">
          {deals.map((d) => (
            <li key={d.name}>
              <button
                type="button"
                onClick={onOpenDeal}
                className="flex w-full items-center justify-between gap-3 rounded-md border border-brand-line bg-brand-paper px-3 py-2.5 text-left transition-colors hover:bg-brand-paper-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] font-medium text-brand-ink">
                    {d.name}
                  </div>
                  <div className="mt-0.5 text-[14px] text-brand-muted">
                    {d.delivered ?? ""}
                  </div>
                </div>
                <span
                  className={
                    "shrink-0 rounded-full border px-2 py-0.5 text-[14px] font-medium " +
                    (d.status === "Delivered"
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
                      : "border-amber-500/40 bg-amber-500/10 text-amber-700")
                  }
                >
                  {d.status}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function DealScreenMock({ onOpenContact }: { onOpenContact?: () => void }) {
  const funnel: Array<{ label: string; n: number; tone?: "accent" | "muted" }> = [
    { label: "Invested", n: 1, tone: "accent" },
    { label: "Prime", n: 2, tone: "accent" },
    { label: "Interested", n: 3, tone: "accent" },
    { label: "Awaiting response", n: 3 },
    { label: "Not yet contacted", n: 105 },
    { label: "Passed (had interest)", n: 1, tone: "muted" },
    { label: "Passed (no interest)", n: 2, tone: "muted" },
    { label: "Do not send", n: 0, tone: "muted" },
  ];
  const rows = [
    {
      name: "Sarah Chen", title: "Managing Director",
      firm: "Stonecreek Capital Partners", firmSub: "Venture Capital · Rating 95",
      loc: "Boston, MA, US", aum: "$1.2B",
      status: "Prime", tags: "Top healthcare VC",
      featured: true,
    },
    {
      name: "David Park", title: "Partner",
      firm: "Polaris Growth Equity", firmSub: "Growth Equity · Rating 93",
      loc: "San Francisco, CA, US", aum: "$3.8B",
      status: "Prime", tags: "—",
    },
    {
      name: "Marcus Williams", title: "VP Corporate Dev",
      firm: "Belvedere Biosciences Fund", firmSub: "Strategic · Rating 92",
      loc: "New York, NY, US", aum: "$850M",
      status: "Interested", tags: "—",
    },
    {
      name: "Lena Park", title: "Principal",
      firm: "Ridgeline Health Partners", firmSub: "Venture Capital · Rating 90",
      loc: "Cambridge, MA, US", aum: "$620M",
      status: "Interested", tags: "—",
    },
    {
      name: "Thomas Reilly", title: "Managing Partner",
      firm: "Beacon Hill Ventures", firmSub: "Venture Capital · Rating 88",
      loc: "Austin, TX, US", aum: "$1.5B",
      status: "Interested", tags: "—",
    },
  ];
  return (
    <div className="rounded-md bg-brand-paper-2 p-3.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-brand-muted">← Dashboard</span>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Edit deal plan</span>
          <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Find more investors</span>
          <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Archive</span>
        </div>
      </div>
      <div className="rounded-md border border-brand-line bg-brand-paper p-3.5">
        <div className="flex items-center gap-2">
          <h3 className="font-serif text-[20px] tracking-[-0.01em] text-brand-ink">
            Solstice BioTherapeutics
          </h3>
          <span className="text-brand-muted" aria-hidden>✎</span>
        </div>
        <div className="mt-0.5 text-[11px] text-brand-muted">
          status active
        </div>

        {/* Deals */}
        <div className="mt-3.5">
          <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">
            Deals
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-brand-line bg-brand-paper-2 px-3 py-2">
            <div className="text-[11.5px] text-brand-ink-2">
              5/24/2026, 5:17:35 PM · <span className="text-emerald-700">complete</span> · target 100 · delivered 100
            </div>
            <div className="flex items-center gap-1.5">
              <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Download</span>
              <span className="rounded-md bg-brand-navy px-2 py-1 text-[14px] font-medium text-brand-paper">Deal Builder</span>
            </div>
          </div>
        </div>

        {/* Investors & Contacts header */}
        <div className="mt-4 border-t border-brand-line pt-3">
          <h4 className="font-serif text-[16px] text-brand-ink">Investors &amp; Contacts</h4>
          <div className="mt-0.5 text-[11px] text-brand-muted">117 firms · 113 in directory</div>
        </div>

        {/* Status funnel */}
        <div className="mt-3">
          <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">
            Status funnel · 117 contacts
          </div>
          <div className="flex flex-wrap gap-1.5">
            {funnel.map((f) => {
              const cls =
                f.tone === "accent"
                  ? "border-brand-accent/40 bg-brand-accent/10 text-brand-accent"
                  : f.tone === "muted"
                  ? "border-brand-line bg-brand-paper-2 text-brand-muted"
                  : "border-brand-line bg-brand-paper text-brand-ink-2";
              return (
                <span
                  key={f.label}
                  className={"rounded-full border px-2 py-0.5 text-[14px] " + cls}
                >
                  {f.label} <span className="font-mono tabular-nums">{f.n}</span>
                </span>
              );
            })}
          </div>
        </div>

        {/* Search + filters */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <div className="flex-1 min-w-[140px] rounded-md border border-brand-line bg-brand-paper-2 px-2.5 py-1.5 text-[14px] text-brand-muted">
            Search contacts…
          </div>
          <span className="rounded-full border border-brand-line bg-brand-paper px-2 py-0.5 text-[14px] text-brand-ink-2">Strategic</span>
          <span className="rounded-full border border-brand-line bg-brand-paper px-2 py-0.5 text-[14px] text-brand-ink-2">Active 2yr</span>
          <span className="rounded-full border border-brand-line bg-brand-paper px-2 py-0.5 text-[14px] text-brand-ink-2">AUM</span>
          <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Rating ↓</span>
        </div>

        {/* Table */}
        <div className="mt-2 overflow-x-auto rounded-md border border-brand-line">
          <table className="w-full min-w-[560px] text-left">
            <thead className="bg-brand-paper-2 text-[9.5px] uppercase tracking-[0.08em] text-brand-muted">
              <tr>
                <th className="px-2.5 py-1.5 font-medium">Contact</th>
                <th className="px-2.5 py-1.5 font-medium">Firm</th>
                <th className="px-2.5 py-1.5 font-medium">Location</th>
                <th className="px-2.5 py-1.5 font-medium">AUM</th>
                <th className="px-2.5 py-1.5 font-medium">Status</th>
                <th className="px-2.5 py-1.5 font-medium">Tags</th>
                <th className="px-2.5 py-1.5 font-medium">Ch.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line bg-brand-paper text-[14px] text-brand-ink-2">
              {rows.map((r) => (
                <tr
                  key={r.name}
                  onClick={r.featured ? onOpenContact : undefined}
                  className={r.featured ? "cursor-pointer hover:bg-brand-paper-2" : ""}
                >
                  <td className="px-2.5 py-1.5">
                    <div className="font-medium text-brand-ink">{r.name}</div>
                    <div className="text-[9.5px] text-brand-muted">{r.title}</div>
                  </td>
                  <td className="px-2.5 py-1.5">
                    <div className="text-brand-ink">{r.firm}</div>
                    <div className="text-[9.5px] text-brand-muted">{r.firmSub}</div>
                  </td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.loc}</td>
                  <td className="px-2.5 py-1.5 font-mono tabular-nums">{r.aum}</td>
                  <td className="px-2.5 py-1.5">
                    <span
                      className={
                        "rounded-full border px-1.5 py-0.5 text-[9.5px] font-medium " +
                        (r.status === "Prime"
                          ? "border-brand-accent/40 bg-brand-accent/10 text-brand-accent"
                          : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700")
                      }
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-2.5 py-1.5 text-[14px] text-brand-muted">{r.tags}</td>
                  <td className="px-2.5 py-1.5 text-[14px] text-brand-muted whitespace-nowrap">
                    ✉ in ↗
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ContactCardMock() {
  return (
    <div className="rounded-md bg-brand-paper-2 p-3.5">
      <div className="mb-2 text-[11px] text-brand-muted">← Back to deal</div>
      <div className="rounded-md border border-brand-line bg-brand-paper p-3.5">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-brand-line pb-3">
          <div className="min-w-0">
            <h3 className="font-serif text-[22px] tracking-[-0.01em] text-brand-ink">
              Sarah Chen
            </h3>
            <div className="mt-0.5 text-[12px] text-brand-ink-2">
              Managing Director, Growth Equity
              <span className="text-brand-muted"> · Stonecreek Capital Partners</span>
            </div>
            <div className="mt-2 inline-flex items-center gap-1 rounded-md border border-brand-accent/40 bg-brand-accent/10 px-2 py-1 text-[14px] font-medium text-brand-accent">
              Prime <span aria-hidden>▼</span>
            </div>
          </div>
          <div className="text-right text-[14px] text-brand-muted">
            Last contact: Today
          </div>
        </div>

        {/* Contact row */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-brand-ink-2">
          <span>✉ <span className="font-mono">s•••••@stonecreek.com</span></span>
          <span>in <span className="font-mono">LinkedIn</span></span>
          <span>📞 <span className="font-mono">(•••) •••-4385</span></span>
        </div>

        {/* Tags */}
        <div className="mt-3">
          <div className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">Tags</div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded-full border border-brand-line bg-brand-paper-2 px-2 py-0.5 text-[14px] text-brand-ink-2">
              Recently led SyntheNeural Series C <span className="text-brand-muted">×</span>
            </span>
            <span className="rounded-md border border-dashed border-brand-line px-2 py-0.5 text-[14px] text-brand-muted">
              Add a tag
            </span>
          </div>
        </div>

        {/* Firm card */}
        <div className="relative mt-4 rounded-md border border-brand-line bg-brand-paper-2 p-3">
          <span className="absolute right-3 top-3 rounded-md bg-brand-ink px-2 py-0.5 text-[9.5px] font-medium text-brand-paper">
            In directory
          </span>
          <div className="font-serif text-[15px] text-brand-ink">Stonecreek Capital Partners</div>
          <div className="text-[14px] text-brand-muted">Venture Capital · Rating 95</div>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
            <dt className="text-brand-muted">AUM</dt><dd className="text-brand-ink-2 font-mono tabular-nums">$1.2B</dd>
            <dt className="text-brand-muted">Location</dt><dd className="text-brand-ink-2">Boston, MA, US</dd>
            <dt className="text-brand-muted">Strategic</dt><dd className="text-brand-ink-2">No</dd>
            <dt className="text-brand-muted"># Investments 2yr</dt><dd className="text-brand-ink-2 font-mono tabular-nums">22</dd>
            <dt className="text-brand-muted">Professionals</dt><dd className="text-brand-ink-2 font-mono tabular-nums">18</dd>
          </dl>
          <p className="mt-2.5 text-[11px] leading-[1.55] text-brand-ink-2">
            Boston-based healthcare and biotech specialist deploying growth-stage capital into clinical-stage therapeutic platforms.
            Twenty-two transactions over the trailing two years across gene therapy, cell therapy, and precision oncology.
            Typical check size $10-50M with leadership preference. Active board involvement and dedicated CMC operating talent in-house.
            Investment committee meets weekly; decisions historically inside six weeks of first meeting.
          </p>
          <div className="mt-2.5 space-y-1.5 text-[14px]">
            <div>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-brand-muted">Stage</span>
              <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">VC-Growth</span>
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">VC-LaterStage</span>
              </span>
            </div>
            <div>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-brand-muted">Industries</span>
              <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">Healthcare</span>
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">Biotech</span>
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">Therapeutics</span>
              </span>
            </div>
            <div>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-brand-muted">Geo</span>
              <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">US</span>
              </span>
            </div>
            <div>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-brand-muted">Check size</span>
              <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">&lt;=$1mm</span>
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">&gt;=$1mm and &lt;=$10mm</span>
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">&gt;=$10mm and &lt;=$50mm</span>
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">&gt;=$50mm and &lt;=$100mm</span>
                <span className="rounded-full border border-brand-line bg-brand-paper px-1.5 py-0.5 text-brand-ink-2">&gt;=$100mm</span>
              </span>
            </div>
          </div>
        </div>

        {/* Why this contact */}
        <div className="mt-4">
          <div className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">Why this contact</div>
          <p className="text-[11.5px] leading-[1.55] text-brand-ink-2">
            Boston-based healthcare-focused MD at Stonecreek with track record in gene therapy and clinical-stage biotech. Recently led SyntheNeural Series C; profile matches Solstice&apos;s stage and sector exactly.
          </p>
        </div>

        {/* Initial outreach */}
        <div className="mt-4">
          <h4 className="font-serif text-[15px] text-brand-ink">Initial outreach</h4>

          {/* Email */}
          <div className="mt-2 rounded-md border border-brand-line bg-brand-paper-2 p-2.5">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">Email</span>
            </div>
            <div className="text-[11px] text-brand-ink-2">
              <span className="text-brand-muted">Subject: </span>
              <span className="font-medium text-brand-ink">Solstice BioTherapeutics: $30M Series C, Phase 2 gene therapy</span>
            </div>
            <p className="mt-2 whitespace-pre-line text-[11.5px] leading-[1.6] text-brand-ink-2">
{`Hi Sarah,

Reaching out on a $30M Series C for Solstice BioTherapeutics, a clinical-stage gene therapy platform with two assets in Phase 2. Lead investor wrote $12M and we have $18M open. Stonecreek's recent bio investments track this profile closely.

Quick call Tuesday or Wednesday to walk through the deck?

Best,
Alex`}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Save</span>
              <span className="rounded-md bg-brand-ink px-2 py-1 text-[14px] font-medium text-brand-paper">Send email</span>
              <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Copy</span>
            </div>
          </div>

          {/* LinkedIn */}
          <div className="mt-2 rounded-md border border-brand-line bg-brand-paper-2 p-2.5">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">LinkedIn</span>
            </div>
            <p className="whitespace-pre-line rounded-md border border-brand-line bg-brand-paper p-2 text-[11.5px] leading-[1.6] text-brand-ink-2">
{`Sarah, sending a $30M Series C deck for a Phase 2 gene therapy platform. Lead committed, $18M open. Please let me know a time that works for a call.`}
            </p>
            <div className="mt-1 text-right text-[14px] text-brand-muted">217 / 300</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Save</span>
              <span className="rounded-md bg-brand-ink px-2 py-1 text-[14px] font-medium text-brand-paper">Send LinkedIn</span>
              <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Copy</span>
            </div>
          </div>
        </div>

        {/* Follow-up */}
        <div className="mt-4">
          <h4 className="font-serif text-[15px] text-brand-ink">Follow-up</h4>
          <div className="mt-2 rounded-md border border-brand-line bg-brand-paper-2 p-2.5">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <div className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">Subject template</div>
                <div className="flex items-center justify-between rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[11px] text-brand-ink-2">
                  <span>[Deal Name] – Time for a Call?</span>
                  <span className="text-brand-muted" aria-hidden>▾</span>
                </div>
              </div>
              <div>
                <div className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">Body template</div>
                <div className="flex items-center justify-between rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[11px] text-brand-ink-2">
                  <span>Are you free for a call with me to discuss?</span>
                  <span className="text-brand-muted" aria-hidden>▾</span>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">Preview</span>
              <span className="text-[14px] text-brand-accent">Reset</span>
            </div>
            <div className="mt-1 rounded-md border border-brand-line bg-brand-paper px-2 py-1.5 text-[11.5px] font-medium text-brand-ink">
              Solstice BioTherapeutics – Time for a Call?
            </div>
            <div className="mt-1 rounded-md border border-brand-line bg-brand-paper px-2 py-1.5 text-[11.5px] text-brand-ink-2">
              Are you free for a call with me to discuss?
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="rounded-md bg-brand-ink px-2 py-1 text-[14px] font-medium text-brand-paper">Send email</span>
              <span className="rounded-md bg-brand-ink px-2 py-1 text-[14px] font-medium text-brand-paper">Send LinkedIn</span>
              <span className="rounded-md border border-brand-line bg-brand-paper px-2 py-1 text-[14px] text-brand-ink-2">Copy</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="mt-4">
          <div className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-muted">Notes</div>
          <div className="rounded-md border border-brand-line bg-brand-paper-2 p-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-brand-accent">Auto-email</div>
                <div className="mt-0.5 text-[11.5px] text-brand-ink-2">Email sent 2026-05-24 18:24</div>
              </div>
              <div className="flex shrink-0 items-center gap-2 text-[14px] text-brand-muted">
                <span>5/24/2026, 6:24:26 PM</span>
                <span className="text-brand-ink-2">Edit</span>
                <span className="text-brand-ink-2">Delete</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
